import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { accountDelete, accountDeletionPlan, deleteAccountRequest, type AccountDeletionPlan, type ApiError } from '@aithora/shared';
import { useSession } from '../session/SessionContext';
import { Wordmark } from '../shell/SideNav';

// "Delete my account" (Google Play and Apple require it; Andrew 2026-10-02). One public
// address for the store listings, /delete-account, drawn like the sign-in pages:
//   - signed in: what is deleted and kept (from the server), then type DELETE to confirm;
//     a resident's request goes to their facility, a team member's to the owner;
//   - signed out: a request form for anyone without the app, answered by email from
//     support@aithoracare.com after we confirm it is them.

const SUPPORT = 'support@aithoracare.com';
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const messageOf = (e: unknown) => ((e as ApiError)?.message ? (e as ApiError).message : 'Something went wrong. Try again.');

export function DeleteAccount() {
  const { session, checking } = useSession();
  // Kept here: deleting signs the person out, which swaps the signed-in view away.
  const [done, setDone] = useState<string | null>(null);
  return (
    <div className="signin">
      <div className="signin__card" data-testid="delete-account">
        <Wordmark />
        {done ? (
          <>
            <h1 className="signin__title">Delete my account</h1>
            <p className="signin__lead" role="status">
              {done}
            </p>
          </>
        ) : checking ? (
          <p className="signin__lead">Checking your session{'…'}</p>
        ) : session ? (
          <SignedIn onDone={setDone} />
        ) : (
          <SignedOut />
        )}
      </div>
    </div>
  );
}

function List({ title, items }: { title: string; items: string[] }) {
  return (
    <>
      <p className="field__label">{title}</p>
      <ul className="signin__lead">
        {items.map((t) => (
          <li key={t}>{t}</li>
        ))}
      </ul>
    </>
  );
}

function SignedIn({ onDone }: { onDone: (message: string) => void }) {
  const { client, signOut } = useSession();
  const [plan, setPlan] = useState<AccountDeletionPlan | null>(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () =>
    accountDeletionPlan(client)
      .then(setPlan)
      .catch((e) => setError(messageOf(e)));
  useEffect(() => {
    void load();
  }, [client]); // load reads only `client`

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (typed.trim() !== 'DELETE') return setError('Type DELETE to confirm.');
    setBusy(true);
    setError(null);
    try {
      const r = await accountDelete(client, { body: { confirm: 'DELETE' } });
      if (r.deleted) {
        onDone(r.message);
        await signOut().catch(() => undefined);
      } else {
        await load();
      }
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  if (!plan) {
    return (
      <>
        <h1 className="signin__title">Delete my account</h1>
        {error ? (
          <p className="signin__error" role="alert">
            {error}
          </p>
        ) : (
          <p className="signin__lead">Loading{'…'}</p>
        )}
      </>
    );
  }
  return (
    <form onSubmit={submit} noValidate>
      <h1 className="signin__title">Delete my account</h1>
      {plan.reason && <p className="signin__lead">{plan.reason}</p>}
      <List title="What is deleted" items={plan.deletes} />
      <List title="What is kept, and why" items={plan.keeps} />
      {plan.pendingRequest ? (
        <p className="signin__lead" role="status">
          {plan.pendingRequest.status === 'forwarded_to_facility' ? 'Your facility has your request and answers within 30 days.' : 'Aithora has your request.'}
        </p>
      ) : plan.mode !== 'refused' ? (
        <>
          <label className="field">
            <span className="field__label">Type DELETE to confirm</span>
            <input className="field__input" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
          </label>
          {error && (
            <p className="signin__error" role="alert">
              {error}
            </p>
          )}
          <button type="submit" className="btn btn--primary" disabled={busy || typed.trim() !== 'DELETE'}>
            {busy ? 'Working…' : plan.mode === 'immediate' ? 'Delete my account' : 'Send my request'}
          </button>
        </>
      ) : null}
      <p className="signin__hint">You can also ask by email to {plan.supportEmail}.</p>
      <div className="signin__links">
        <Link className="linkbtn" to="/">
          Back to Aithora
        </Link>
      </div>
    </form>
  );
}

function SignedOut() {
  const { client } = useSession();
  const [f, setF] = useState({ email: '', name: '', details: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!EMAIL.test(f.email.trim())) return setError('Enter the email address of your account.');
    setBusy(true);
    setError(null);
    try {
      const r = await deleteAccountRequest(client, {
        body: { email: f.email.trim(), ...(f.name.trim() ? { name: f.name.trim() } : {}), ...(f.details.trim() ? { details: f.details.trim() } : {}) },
      });
      setSent(r.message);
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <>
        <h1 className="signin__title">Request received</h1>
        <p className="signin__lead" role="status">
          {sent}
        </p>
        <div className="signin__links">
          <Link className="linkbtn" to="/signin">
            Back to sign in
          </Link>
        </div>
      </>
    );
  }
  return (
    <form onSubmit={submit} noValidate>
      <h1 className="signin__title">Delete my account</h1>
      <p className="signin__lead">
        Ask us to delete your Aithora account and the personal data we hold. We email you to confirm it is you, then delete it within 30 days. If you can sign in, you can also do it at once in the app under Me, or here after signing in.
      </p>
      <p className="signin__lead">
        A resident's care records belong to their facility, so we pass a resident's request to the facility. Records the law requires us to keep (the audit trail, billing and consent records) are kept without your contact details.
      </p>
      <label className="field">
        <span className="field__label">Email of the account</span>
        <input className="field__input" type="email" autoComplete="email" value={f.email} onChange={set('email')} required />
      </label>
      <label className="field">
        <span className="field__label">Your name (optional)</span>
        <input className="field__input" autoComplete="name" value={f.name} onChange={set('name')} />
      </label>
      <label className="field">
        <span className="field__label">Anything we should know (optional)</span>
        <input className="field__input" value={f.details} onChange={set('details')} />
      </label>
      {error && (
        <p className="signin__error" role="alert">
          {error}
        </p>
      )}
      <button type="submit" className="btn btn--primary" disabled={busy}>
        {busy ? 'Sending…' : 'Send request'}
      </button>
      <p className="signin__hint">Or email {SUPPORT} from the address on your account.</p>
      <div className="signin__links">
        <Link className="linkbtn" to="/signin">
          Sign in instead
        </Link>
      </div>
    </form>
  );
}
